import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { 
    Calendar, 
    MessageSquare, 
    Users, 
    BarChart3, 
    Globe, 
    CreditCard,
    Menu,
    X,
    ArrowRight,
    Check,
    Star,
    Mail,
    Phone,
    Send,
    Clock,
    MapPin,
    Scissors,
    Search,
    ExternalLink,
    ChevronRight,
    Sparkles
} from "lucide-react";

// ==================== FONT STYLES ====================
const TYPOGRAPHY = {
    heading: "font-medium tracking-tight", // Arial for headings
    subheading: "font-light tracking-wide", 
    body: "font-normal leading-relaxed", 
    accent: "font-medium tracking-wide italic", 
    button: "font-medium tracking-wide", 
    label: "font-medium tracking-widest uppercase", 
    mono: "font-mono", // For technical text
};

// ==================== CONSTANTS ====================
const COLORS = {
    primaryDark: '#12006d',
    accentBlue: '#4fcdfa',
    highlightGold: '#eac26b',
};

const CONTACT = {
    email: 'hello@trimyapp.com',
    support: 'support@trimyapp.com',
    phone: '+233 24 896 5717',
    whatsapp: '+1-682 559 0277',
    whatsappUrl: 'https://wa.me/16825590277',
    address: 'Accra, Ghana',
};

const FEATURES = [
    {
        id: 'booking',
        title: 'Smart Online Booking',
        description: 'Empower your clients to book appointments 24/7 with an intuitive scheduling interface and QR code integration.',
        icon: Calendar,
    },
    {
        id: 'reminders',
        title: 'Automatic Reminders',
        description: 'Reduce no-shows by up to 80% with automated SMS and Email reminders sent to your clients.',
        icon: MessageSquare,
    },
    {
        id: 'staff',
        title: 'Staff Management',
        description: 'Let clients choose their favorite service providers and manage staff schedules with ease.',
        icon: Users,
    },
    {
        id: 'analytics',
        title: 'Business Analytics',
        description: 'Track your expenses, revenue, and growth with our powerful real-time dashboard.',
        icon: BarChart3,
    },
    {
        id: 'website',
        title: 'Mini Booking Site',
        description: 'Every business gets a beautiful, SEO-optimized booking page to showcase their brand.',
        icon: Globe,
    },
    {
        id: 'payments',
        title: 'Paystack Integration',
        description: 'Securely accept payments and booking deposits directly through the app via Paystack.',
        icon: CreditCard,
    },
];

const PRICING = [
    {
        name: 'Starter',
        price: 'Free',
        period: 'forever',
        description: 'Perfect for getting started',
        features: [
            'Up to 50 appointments/month',
            'Basic booking page',
            'Email reminders',
            'Single staff account',
        ],
        cta: 'Get Started',
        popular: false,
    },
    {
        name: 'Professional',
        price: 'GHS 99',
        period: '/month',
        description: 'For growing businesses',
        features: [
            'Unlimited appointments',
            'Custom booking page',
            'SMS & Email reminders',
            'Up to 5 staff accounts',
            'Payment collection',
            'Business analytics',
        ],
        cta: 'Start Free Trial',
        popular: true,
    },
    {
        name: 'Enterprise',
        price: 'Custom',
        period: '',
        description: 'For large businesses',
        features: [
            'Everything in Professional',
            'Unlimited staff accounts',
            'Priority support',
            'Custom integrations',
            'Dedicated account manager',
        ],
        cta: 'Contact Sales',
        popular: false,
    },
];

// ==================== NAVBAR COMPONENT ====================
function Navbar({ activeTab, setActiveTab }) {
    const [isOpen, setIsOpen] = useState(false);
    const [scrolled, setScrolled] = useState(false);

    useEffect(() => {
        const handleScroll = () => setScrolled(window.scrollY > 20);
        window.addEventListener('scroll', handleScroll);
        return () => window.removeEventListener('scroll', handleScroll);
    }, []);

    const tabs = [
        { id: 'home', label: 'Home' },
        { id: 'businesses', label: 'Businesses' },
        { id: 'contact', label: 'Contact' },
    ];

    return (
        <nav className={`fixed top-0 w-full z-50 transition-all duration-500 ${scrolled ? 'bg-[#12006d]/95 backdrop-blur-md py-3 shadow-xl' : 'bg-[#12006d] py-4'}`}>
            <div className="container mx-auto px-4 md:px-6 flex items-center justify-between">
                <a href="#" className="flex items-center gap-2 group" onClick={() => setActiveTab('home')}>
                    <div className="w-10 h-10 bg-[#4fcdfa] rounded-xl flex items-center justify-center rotate-3 group-hover:rotate-12 transition-transform duration-300">
                        <span className="text-[#12006d] font-bold text-xl">T</span>
                    </div>
                    <span className={`text-white text-2xl tracking-tight ${TYPOGRAPHY.heading}`}>Trimy</span>
                </a>

                {/* Desktop Tabs */}
                <div className="hidden md:flex items-center gap-1 bg-white/5 rounded-full p-1">
                    {tabs.map((tab) => (
                        <button
                            key={tab.id}
                            onClick={() => setActiveTab(tab.id)}
                            className={`px-6 py-2 rounded-full transition-all ${TYPOGRAPHY.button} ${
                                activeTab === tab.id
                                    ? 'bg-[#4fcdfa] text-[#12006d]'
                                    : 'text-slate-300 hover:text-white'
                            }`}
                        >
                            {tab.label}
                        </button>
                    ))}
                </div>

                {/* Desktop CTA */}
                <div className="hidden md:flex items-center gap-4">
                    <Link to="/login" className={`text-slate-300 hover:text-[#4fcdfa] transition-colors ${TYPOGRAPHY.button}`}>
                        Login
                    </Link>
                    <Link to="/register" className={`bg-[#4fcdfa] hover:bg-[#3bbde9] text-[#12006d] px-5 py-2.5 rounded-full transition-all shadow-lg shadow-[#4fcdfa]/20 ${TYPOGRAPHY.button}`}>
                        Start Free Trial
                    </Link>
                </div>

                {/* Mobile Toggle */}
                <button className="md:hidden text-white p-2" onClick={() => setIsOpen(!isOpen)}>
                    {isOpen ? <X size={28} /> : <Menu size={28} />}
                </button>
            </div>

            {/* Mobile Menu */}
            <div className={`fixed inset-0 bg-[#12006d] z-40 transition-transform duration-500 ease-in-out transform ${isOpen ? 'translate-x-0' : 'translate-x-full'} md:hidden`}>
                <div className="flex flex-col items-center justify-center h-full gap-6 px-6 text-center">
                    {tabs.map((tab) => (
                        <button
                            key={tab.id}
                            onClick={() => { setActiveTab(tab.id); setIsOpen(false); }}
                            className={`text-2xl ${activeTab === tab.id ? 'text-[#4fcdfa]' : 'text-white'} ${TYPOGRAPHY.heading}`}
                        >
                            {tab.label}
                        </button>
                    ))}
                    <hr className="w-20 border-slate-700 my-4" />
                    <Link to="/login" onClick={() => setIsOpen(false)} className={`text-xl text-slate-300 ${TYPOGRAPHY.button}`}>Login</Link>
                    <Link to="/register" onClick={() => setIsOpen(false)} className={`w-full bg-[#4fcdfa] text-[#12006d] py-4 rounded-full text-lg ${TYPOGRAPHY.button}`}>
                        Start Free Trial
                    </Link>
                </div>
            </div>
        </nav>
    );
}

// ==================== HERO SECTION ====================
function Hero() {
    return (
        <section className="relative min-h-screen flex items-center pt-24 pb-16 lg:pt-32 overflow-hidden bg-[#12006d]">
            {/* Background blobs */}
            <div className="absolute top-1/4 -left-20 w-72 h-72 md:w-96 md:h-96 bg-[#4fcdfa]/20 blur-[100px] rounded-full animate-pulse" />
            <div className="absolute bottom-1/4 -right-20 w-72 h-72 md:w-96 md:h-96 bg-[#eac26b]/10 blur-[100px] rounded-full animate-pulse" />
            
            <div className="container mx-auto px-4 md:px-6 relative z-10">
                <div className="grid lg:grid-cols-2 gap-12 lg:gap-16 items-center">
                    <div className="text-center lg:text-left">
                        <div className={`inline-flex items-center gap-2 px-4 py-2 rounded-full bg-white/5 border border-white/10 text-[#4fcdfa] text-sm mb-6 ${TYPOGRAPHY.label}`}>
                            <Sparkles className="w-4 h-4" />
                            Empowering service businesses across Africa
                        </div>
                        
                        <h1 className={`text-4xl md:text-5xl lg:text-6xl text-white leading-[1.1] mb-6 ${TYPOGRAPHY.heading}`}>
                            Run Your Business. <br />
                            <span className={`bg-gradient-to-r from-[#4fcdfa] to-[#eac26b] bg-clip-text text-transparent ${TYPOGRAPHY.accent}`}>
                                Not Your Stress.
                            </span>
                        </h1>
                        
                        <p className={`text-base md:text-lg text-slate-300 mb-10 max-w-xl mx-auto lg:mx-0 ${TYPOGRAPHY.body}`}>
                            Trimy is the all-in-one platform for salons, spas, and service providers to manage bookings, 
                            automate reminders, and grow revenue effortlessly.
                        </p>
                        
                        <div className="flex flex-col sm:flex-row gap-4 justify-center lg:justify-start">
                            <Link to="/register" className={`bg-[#4fcdfa] hover:bg-[#3bbde9] text-[#12006d] px-8 py-4 rounded-full text-lg transition-all shadow-lg shadow-[#4fcdfa]/30 flex items-center justify-center gap-2 ${TYPOGRAPHY.button}`}>
                                Start 7-Day Free Trial
                                <ArrowRight className="w-5 h-5" />
                            </Link>
                            <Link to="/login" className={`border-2 border-white/20 hover:border-white/40 text-white px-8 py-4 rounded-full text-lg transition-all flex items-center justify-center ${TYPOGRAPHY.button}`}>
                                Login to Dashboard
                            </Link>
                        </div>
                        
                        <div className="mt-12 flex flex-col sm:flex-row items-center justify-center lg:justify-start gap-6">
                            <div className="flex -space-x-3">
                                {[1,2,3,4].map(i => (
                                    <div key={i} className="w-10 h-10 rounded-full border-2 border-[#12006d] bg-gradient-to-br from-[#4fcdfa] to-[#12006d] flex items-center justify-center text-white text-xs font-medium">
                                        {String.fromCharCode(64 + i)}
                                    </div>
                                ))}
                            </div>
                            <p className={`text-slate-400 text-sm ${TYPOGRAPHY.body}`}>
                                Join <span className="text-white font-medium">500+</span> businesses growing with Trimy
                            </p>
                        </div>
                    </div>

                    {/* Dashboard Mockup */}
                    <div className="relative mt-8 lg:mt-0 px-4 md:px-0">
                        <div className="absolute inset-0 bg-gradient-to-br from-[#4fcdfa]/40 to-transparent blur-3xl opacity-30" />
                        
                        <div className="relative bg-white/5 backdrop-blur-sm p-4 md:p-6 rounded-[2rem] border border-white/10 shadow-2xl max-w-lg mx-auto">
                            <div className="flex items-center justify-between mb-6">
                                <div className="flex gap-1.5">
                                    <div className="w-3 h-3 rounded-full bg-red-500" />
                                    <div className="w-3 h-3 rounded-full bg-yellow-500" />
                                    <div className="w-3 h-3 rounded-full bg-green-500" />
                                </div>
                                <div className={`text-xs text-slate-400 ${TYPOGRAPHY.mono}`}>trimyapp.com/dashboard</div>
                            </div>

                            <div className="space-y-4">
                                <div className="grid grid-cols-3 gap-3">
                                    <div className="p-3 bg-white/5 rounded-xl border border-white/5">
                                        <Calendar className="text-[#4fcdfa] w-5 h-5 mb-2" />
                                        <div className="text-xs text-slate-400">Today</div>
                                        <div className={`text-lg text-white ${TYPOGRAPHY.heading}`}>12</div>
                                    </div>
                                    <div className="p-3 bg-[#4fcdfa]/10 rounded-xl border border-[#4fcdfa]/20">
                                        <BarChart3 className="text-white w-5 h-5 mb-2" />
                                        <div className="text-xs text-slate-400">Revenue</div>
                                        <div className={`text-lg text-white ${TYPOGRAPHY.heading}`}>₵2.4k</div>
                                    </div>
                                    <div className="p-3 bg-white/5 rounded-xl border border-white/5">
                                        <Users className="text-[#eac26b] w-5 h-5 mb-2" />
                                        <div className="text-xs text-slate-400">Clients</div>
                                        <div className={`text-lg text-white ${TYPOGRAPHY.heading}`}>156</div>
                                    </div>
                                </div>

                                <div className="h-32 w-full bg-white/5 rounded-xl border border-white/5 p-4">
                                    <div className="flex items-end justify-between h-full gap-2">
                                        {[35, 65, 45, 90, 55, 85, 40, 75, 50, 60].map((h, i) => (
                                            <div 
                                                key={i} 
                                                className="bg-[#4fcdfa] w-full rounded-t-sm transition-all duration-1000" 
                                                style={{ height: `${h}%`, opacity: 0.4 + (h/200) }} 
                                            />
                                        ))}
                                    </div>
                                </div>

                                <div className="p-3 bg-green-500/10 rounded-xl border border-green-500/20 flex items-center gap-3">
                                    <div className="w-8 h-8 bg-green-500 rounded-full flex items-center justify-center">
                                        <Check className="w-4 h-4 text-white" />
                                    </div>
                                    <div>
                                        <div className="text-xs text-green-400">New Booking</div>
                                        <div className={`text-sm text-white ${TYPOGRAPHY.heading}`}>Maasi. - Haircut at 2:00 PM</div>
                                    </div>
                                </div>
                            </div>
                        </div>
                        
                        {/* Floating badge */}
                        <div className="absolute -bottom-4 -left-4 bg-white/10 backdrop-blur-sm p-4 rounded-xl shadow-2xl flex items-center gap-3 border border-white/10">
                            <div className="bg-[#eac26b] p-2 rounded-lg text-[#12006d]">
                                <CreditCard size={20} />
                            </div>
                            <div>
                                <div className="text-xs text-slate-400">Today's Revenue</div>
                                <div className={`text-lg text-white ${TYPOGRAPHY.heading}`}>₵1,240.00</div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </section>
    );
}

// ==================== FEATURES SECTION ====================
function Features() {
    return (
        <section className="py-20 bg-white">
            <div className="container mx-auto px-4 md:px-6">
                <div className="text-center max-w-3xl mx-auto mb-16">
                    <h2 className={`text-[#12006d] text-sm mb-4 ${TYPOGRAPHY.label}`}>Features</h2>
                    <h3 className={`text-3xl md:text-4xl text-slate-900 mb-6 ${TYPOGRAPHY.heading}`}>
                        Everything You Need to Scale Your Business
                    </h3>
                    <p className={`text-lg text-slate-600 ${TYPOGRAPHY.body}`}>
                        From automated scheduling to financial reporting, Trimy handles the complexity so you can focus on your craft.
                    </p>
                </div>

                <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
                    {FEATURES.map((feature, index) => (
                        <div
                            key={feature.id}
                            className="p-6 rounded-2xl border border-slate-100 bg-slate-50 hover:bg-white hover:shadow-xl hover:border-[#4fcdfa]/30 transition-all group"
                        >
                            <div className="w-12 h-12 bg-white rounded-xl shadow-sm flex items-center justify-center text-[#12006d] mb-4 group-hover:bg-[#4fcdfa] group-hover:text-white transition-colors">
                                <feature.icon className="w-6 h-6" />
                            </div>
                            <h4 className={`text-lg text-slate-900 mb-2 ${TYPOGRAPHY.heading}`}>{feature.title}</h4>
                            <p className={`text-slate-600 leading-relaxed text-sm ${TYPOGRAPHY.body}`}>
                                {feature.description}
                            </p>
                        </div>
                    ))}
                </div>
            </div>
        </section>
    );
}

// ==================== HOW IT WORKS SECTION ====================
function HowItWorks() {
    const steps = [
        { number: '01', title: 'Sign Up', description: 'Create your free account in under 2 minutes. No credit card required.' },
        { number: '02', title: 'Set Up Your Business', description: 'Add your services, staff, and working hours. Customize your booking page.' },
        { number: '03', title: 'Share Your Link', description: 'Share your booking link via QR code, social media, or your website.' },
        { number: '04', title: 'Grow Your Business', description: 'Watch bookings come in and let Trimy handle reminders automatically.' },
    ];

    return (
        <section className="py-20 bg-[#12006d]">
            <div className="container mx-auto px-4 md:px-6">
                <div className="text-center max-w-3xl mx-auto mb-16">
                    <h2 className={`text-[#4fcdfa] text-sm mb-4 ${TYPOGRAPHY.label}`}>How It Works</h2>
                    <h3 className={`text-3xl md:text-4xl text-white mb-6 ${TYPOGRAPHY.heading}`}>
                        Get Started in Minutes
                    </h3>
                </div>

                <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
                    {steps.map((step, index) => (
                        <div key={index} className="relative">
                            <div className="p-6 bg-white/5 rounded-2xl border border-white/10 hover:bg-white/10 transition-all">
                                <div className={`text-5xl text-[#4fcdfa]/20 mb-4 ${TYPOGRAPHY.heading}`}>{step.number}</div>
                                <h4 className={`text-xl text-white mb-2 ${TYPOGRAPHY.heading}`}>{step.title}</h4>
                                <p className={`text-slate-400 text-sm ${TYPOGRAPHY.body}`}>{step.description}</p>
                            </div>
                            {index < steps.length - 1 && (
                                <ChevronRight className="hidden lg:block absolute top-1/2 -right-3 text-[#4fcdfa]/30 w-6 h-6" />
                            )}
                        </div>
                    ))}
                </div>
            </div>
        </section>
    );
}

// ==================== PRICING SECTION ====================
function Pricing() {
    return (
        <section className="py-20 bg-slate-50">
            <div className="container mx-auto px-4 md:px-6">
                <div className="text-center max-w-3xl mx-auto mb-16">
                    <h2 className={`text-[#12006d] text-sm mb-4 ${TYPOGRAPHY.label}`}>Pricing</h2>
                    <h3 className={`text-3xl md:text-4xl text-slate-900 mb-6 ${TYPOGRAPHY.heading}`}>
                        Simple, Transparent Pricing
                    </h3>
                    <p className={`text-lg text-slate-600 ${TYPOGRAPHY.body}`}>
                        Start free, upgrade when you're ready. No hidden fees.
                    </p>
                </div>

                <div className="grid md:grid-cols-3 gap-6 max-w-5xl mx-auto">
                    {PRICING.map((plan, index) => (
                        <div
                            key={index}
                            className={`p-6 rounded-2xl ${
                                plan.popular
                                    ? 'bg-[#12006d] text-white ring-4 ring-[#4fcdfa] scale-105'
                                    : 'bg-white border border-slate-200'
                            }`}
                        >
                            {plan.popular && (
                                <div className={`inline-block bg-[#4fcdfa] text-[#12006d] text-xs px-3 py-1 rounded-full mb-4 ${TYPOGRAPHY.label}`}>
                                    Most Popular
                                </div>
                            )}
                            <h4 className={`text-xl mb-2 ${plan.popular ? 'text-white' : 'text-slate-900'} ${TYPOGRAPHY.heading}`}>
                                {plan.name}
                            </h4>
                            <p className={`text-sm mb-4 ${plan.popular ? 'text-slate-300' : 'text-slate-600'} ${TYPOGRAPHY.body}`}>
                                {plan.description}
                            </p>
                            <div className="mb-6">
                                <span className={`text-4xl ${plan.popular ? 'text-white' : 'text-slate-900'} ${TYPOGRAPHY.heading}`}>
                                    {plan.price}
                                </span>
                                <span className={plan.popular ? 'text-slate-300' : 'text-slate-600'}>
                                    {plan.period}
                                </span>
                            </div>
                            <ul className="space-y-3 mb-6">
                                {plan.features.map((feature, i) => (
                                    <li key={i} className={`flex items-center gap-2 text-sm ${TYPOGRAPHY.body}`}>
                                        <Check className={`w-4 h-4 ${plan.popular ? 'text-[#4fcdfa]' : 'text-green-500'}`} />
                                        <span className={plan.popular ? 'text-slate-300' : 'text-slate-600'}>
                                            {feature}
                                        </span>
                                    </li>
                                ))}
                            </ul>
                            <Link
                                to="/register"
                                className={`block w-full py-3 rounded-full text-center transition-all ${TYPOGRAPHY.button} ${
                                    plan.popular
                                        ? 'bg-[#4fcdfa] text-[#12006d] hover:bg-[#3bbde9]'
                                        : 'bg-[#12006d] text-white hover:bg-[#1a0090]'
                                }`}
                            >
                                {plan.cta}
                            </Link>
                        </div>
                    ))}
                </div>
            </div>
        </section>
    );
}

// ==================== BUSINESSES TAB ====================
function BusinessesTab() {
    const [businesses, setBusinesses] = useState([]);
    const [loading, setLoading] = useState(true);
    const [searchQuery, setSearchQuery] = useState('');

    useEffect(() => {
        loadBusinesses();
    }, []);

    const loadBusinesses = async () => {
        try {
            const API_BASE = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8000';
            const response = await fetch(`${API_BASE}/api/public/businesses`);
            const data = await response.json();
            setBusinesses(data.businesses || []);
        } catch (error) {
            console.error('Failed to load businesses:', error);
            // Use sample data for now
            setBusinesses([
                { id: '1', business_name: 'Glow Salon', description: 'Premier hair and beauty salon', address: 'Accra, Ghana', phone: '+233 20 000 0001' },
                { id: '2', business_name: 'The Cut Barbershop', description: 'Professional barber services', address: 'Kumasi, Ghana', phone: '+233 20 000 0002' },
                { id: '3', business_name: 'Pure Spa & Wellness', description: 'Relaxation and wellness center', address: 'Tema, Ghana', phone: '+233 20 000 0003' },
            ]);
        } finally {
            setLoading(false);
        }
    };

    const filteredBusinesses = businesses.filter(b => 
        b.business_name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
        b.address?.toLowerCase().includes(searchQuery.toLowerCase())
    );

    return (
        <section className="min-h-screen pt-24 pb-16 bg-slate-50">
            <div className="container mx-auto px-4 md:px-6">
                {/* Header */}
                <div className="text-center max-w-3xl mx-auto mb-12">
                    <h1 className={`text-3xl md:text-4xl text-slate-900 mb-4 ${TYPOGRAPHY.heading}`}>
                        Find & Book Services
                    </h1>
                    <p className={`text-lg text-slate-600 mb-8 ${TYPOGRAPHY.body}`}>
                        Discover amazing salons, spas, and service providers on Trimy
                    </p>
                    
                    {/* Search */}
                    <div className="relative max-w-md mx-auto">
                        <Search className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-400 w-5 h-5" />
                        <input
                            type="text"
                            placeholder="Search businesses..."
                            value={searchQuery}
                            onChange={(e) => setSearchQuery(e.target.value)}
                            className="w-full pl-12 pr-4 py-3 rounded-full border border-slate-200 focus:border-[#4fcdfa] focus:ring-2 focus:ring-[#4fcdfa]/20 outline-none transition-all"
                        />
                    </div>
                </div>

                {/* Business Grid */}
                {loading ? (
                    <div className="flex items-center justify-center py-20">
                        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#12006d]"></div>
                    </div>
                ) : filteredBusinesses.length === 0 ? (
                    <div className="text-center py-20">
                        <Scissors className="w-16 h-16 mx-auto text-slate-300 mb-4" />
                        <p className="text-slate-600">No businesses found</p>
                    </div>
                ) : (
                    <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
                        {filteredBusinesses.map((business) => (
                            <div
                                key={business.id}
                                className="bg-white rounded-2xl border border-slate-100 overflow-hidden hover:shadow-xl hover:border-[#4fcdfa]/30 transition-all group"
                            >
                                {/* Business Image/Banner */}
                                <div className="h-32 bg-gradient-to-br from-[#12006d] to-[#4fcdfa] flex items-center justify-center">
                                    <div className="w-16 h-16 bg-white/20 backdrop-blur-sm rounded-2xl flex items-center justify-center">
                                        <Scissors className="w-8 h-8 text-white" />
                                    </div>
                                </div>
                                
                                <div className="p-5">
                                    <h3 className={`text-lg text-slate-900 mb-1 ${TYPOGRAPHY.heading}`}>
                                        {business.business_name}
                                    </h3>
                                    {business.description && (
                                        <p className={`text-sm text-slate-600 mb-3 line-clamp-2 ${TYPOGRAPHY.body}`}>
                                            {business.description}
                                        </p>
                                    )}
                                    
                                    <div className="space-y-2 mb-4">
                                        {business.address && (
                                            <div className={`flex items-center gap-2 text-sm text-slate-500 ${TYPOGRAPHY.body}`}>
                                                <MapPin className="w-4 h-4" />
                                                <span>{business.address}</span>
                                            </div>
                                        )}
                                        {business.phone && (
                                            <div className={`flex items-center gap-2 text-sm text-slate-500 ${TYPOGRAPHY.body}`}>
                                                <Phone className="w-4 h-4" />
                                                <span>{business.phone}</span>
                                            </div>
                                        )}
                                    </div>
                                    
                                    <Link
                                        to={`/book/${business.id}`}
                                        className={`flex items-center justify-center gap-2 w-full py-2.5 bg-[#12006d] hover:bg-[#1a0090] text-white rounded-xl transition-all group-hover:bg-[#4fcdfa] group-hover:text-[#12006d] ${TYPOGRAPHY.button}`}
                                    >
                                        Book Now
                                        <ExternalLink className="w-4 h-4" />
                                    </Link>
                                </div>
                            </div>
                        ))}
                    </div>
                )}

                {/* CTA for businesses */}
                <div className="mt-16 text-center p-8 bg-[#12006d] rounded-3xl">
                    <h3 className={`text-2xl text-white mb-4 ${TYPOGRAPHY.heading}`}>
                        Are you a business owner?
                    </h3>
                    <p className={`text-slate-300 mb-6 ${TYPOGRAPHY.body}`}>
                        Join Trimy and get your business listed. Start accepting online bookings today!
                    </p>
                    <Link
                        to="/register"
                        className={`inline-flex items-center gap-2 bg-[#4fcdfa] text-[#12006d] px-6 py-3 rounded-full hover:bg-[#3bbde9] transition-all ${TYPOGRAPHY.button}`}
                    >
                        Register Your Business
                        <ArrowRight className="w-5 h-5" />
                    </Link>
                </div>
            </div>
        </section>
    );
}

// ==================== CONTACT TAB ====================
function ContactTab() {
    return (
        <section className="min-h-screen pt-24 pb-16 bg-[#12006d]">
            <div className="container mx-auto px-4 md:px-6">
                <div className="max-w-5xl mx-auto">
                    {/* Header */}
                    <div className="text-center mb-12">
                        <h1 className={`text-3xl md:text-4xl text-white mb-4 ${TYPOGRAPHY.heading}`}>
                            Get in Touch
                        </h1>
                        <p className={`text-lg text-slate-300 ${TYPOGRAPHY.body}`}>
                            We're here to help. Reach out anytime!
                        </p>
                    </div>

                    {/* Contact Card */}
                    <div className="bg-white/5 rounded-3xl p-6 md:p-10 border border-white/10 backdrop-blur-sm">
                        <div className="grid lg:grid-cols-2 gap-10">
                            {/* Contact Info */}
                            <div>
                                <h2 className={`text-[#eac26b] text-sm uppercase mb-4 ${TYPOGRAPHY.label}`}>
                                    Support & Sales
                                </h2>
                                <h3 className={`text-2xl md:text-3xl text-white mb-6 ${TYPOGRAPHY.heading}`}>
                                    Need Help? <br />
                                    <span className="text-[#4fcdfa]">We're Here for You.</span>
                                </h3>
                                <p className={`text-slate-300 mb-8 leading-relaxed ${TYPOGRAPHY.body}`}>
                                    Whether you're just starting out or scaling your business, 
                                    our dedicated team is available to assist you.
                                </p>
                                
                                <div className="flex items-center gap-4 mb-6">
                                    <div className="bg-[#4fcdfa]/20 p-3 rounded-xl text-[#4fcdfa]">
                                        <Clock size={24} />
                                    </div>
                                    <div>
                                        <div className="text-sm text-slate-400">Available</div>
                                        <div className={`text-white ${TYPOGRAPHY.heading}`}>Monday - Saturday, 8AM - 6PM</div>
                                    </div>
                                </div>

                                <div className="flex items-center gap-4">
                                    <div className="bg-[#eac26b]/20 p-3 rounded-xl text-[#eac26b]">
                                        <MapPin size={24} />
                                    </div>
                                    <div>
                                        <div className="text-sm text-slate-400">Location</div>
                                        <div className={`text-white ${TYPOGRAPHY.heading}`}>{CONTACT.address}</div>
                                    </div>
                                </div>
                            </div>
                            
                            {/* Contact Methods */}
                            <div className="space-y-4">
                                {/* WhatsApp */}
                                <a 
                                    href={CONTACT.whatsappUrl} 
                                    target="_blank" 
                                    rel="noopener noreferrer" 
                                    className="block p-5 bg-[#4fcdfa]/10 hover:bg-[#4fcdfa]/20 rounded-2xl border border-[#4fcdfa]/30 transition-all group"
                                >
                                    <div className="flex items-center gap-4">
                                        <div className="bg-[#4fcdfa] p-3 rounded-full text-[#12006d]">
                                            <Send size={20} />
                                        </div>
                                        <div className="flex-1">
                                            <div className="flex items-center gap-2 mb-1">
                                                <span className={`text-xs text-slate-400 uppercase tracking-widest ${TYPOGRAPHY.label}`}>Fastest Response</span>
                                                <span className={`bg-[#4fcdfa]/20 text-[#4fcdfa] text-[10px] px-2 py-0.5 rounded-full ${TYPOGRAPHY.label}`}>WHATSAPP</span>
                                            </div>
                                            <div className={`text-lg text-white group-hover:text-[#4fcdfa] transition-colors ${TYPOGRAPHY.heading}`}>
                                                {CONTACT.whatsapp}
                                            </div>
                                        </div>
                                        <ExternalLink className="w-5 h-5 text-slate-400 group-hover:text-[#4fcdfa]" />
                                    </div>
                                </a>

                                {/* Phone */}
                                <a 
                                    href={`tel:${CONTACT.phone}`}
                                    className="block p-5 bg-white/5 hover:bg-white/10 rounded-2xl border border-white/10 transition-all group"
                                >
                                    <div className="flex items-center gap-4">
                                        <div className="bg-[#eac26b]/20 p-3 rounded-full text-[#eac26b]">
                                            <Phone size={20} />
                                        </div>
                                        <div className="flex-1">
                                            <div className={`text-xs text-slate-400 uppercase mb-1 ${TYPOGRAPHY.label}`}>Phone</div>
                                            <div className={`text-lg text-white group-hover:text-[#eac26b] transition-colors ${TYPOGRAPHY.heading}`}>
                                                {CONTACT.phone}
                                            </div>
                                        </div>
                                    </div>
                                </a>

                                {/* Emails */}
                                <div className="grid sm:grid-cols-2 gap-4">
                                    <a 
                                        href={`mailto:${CONTACT.email}`}
                                        className="block p-5 bg-white/5 hover:bg-white/10 rounded-2xl border border-white/10 transition-all"
                                    >
                                        <Mail className="text-[#4fcdfa] mb-3" size={20} />
                                        <div className={`text-xs text-slate-400 uppercase mb-1 ${TYPOGRAPHY.label}`}>General</div>
                                        <div className={`text-sm text-white truncate ${TYPOGRAPHY.heading}`}>{CONTACT.email}</div>
                                    </a>
                                    
                                    <a 
                                        href={`mailto:${CONTACT.support}`}
                                        className="block p-5 bg-white/5 hover:bg-white/10 rounded-2xl border border-white/10 transition-all"
                                    >
                                        <MessageSquare className="text-[#eac26b] mb-3" size={20} />
                                        <div className={`text-xs text-slate-400 uppercase mb-1 ${TYPOGRAPHY.label}`}>Support</div>
                                        <div className={`text-sm text-white truncate ${TYPOGRAPHY.heading}`}>{CONTACT.support}</div>
                                    </a>
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* FAQ Teaser */}
                    <div className="mt-12 text-center">
                        <p className={`text-slate-400 ${TYPOGRAPHY.body}`}>
                            Have questions? Check out our{' '}
                            <a href="#" className="text-[#4fcdfa] hover:underline">FAQ</a>
                            {' '}or{' '}
                            <a href="#" className="text-[#4fcdfa] hover:underline">Help Center</a>
                        </p>
                    </div>
                </div>
            </div>
        </section>
    );
}

// ==================== FOOTER COMPONENT ====================
function Footer() {
    return (
        <footer className="bg-[#0a0042] pt-16 pb-8 text-white">
            <div className="container mx-auto px-4 md:px-6">
                <div className="grid grid-cols-2 md:grid-cols-4 gap-8 mb-12">
                    {/* Brand */}
                    <div className="col-span-2 md:col-span-1">
                        <div className="flex items-center gap-2 mb-4">
                            <div className="w-8 h-8 bg-[#4fcdfa] rounded-lg flex items-center justify-center">
                                <span className="text-[#12006d] font-bold">T</span>
                            </div>
                            <span className={`text-xl ${TYPOGRAPHY.heading}`}>Trimy</span>
                        </div>
                        <p className={`text-slate-400 text-sm leading-relaxed ${TYPOGRAPHY.body}`}>
                            The all-in-one platform for service businesses to manage bookings and grow.
                        </p>
                    </div>

                    {/* Platform */}
                    <div>
                        <h4 className={`mb-4 text-[#4fcdfa] ${TYPOGRAPHY.heading}`}>Platform</h4>
                        <ul className={`space-y-2 text-sm text-slate-400 ${TYPOGRAPHY.body}`}>
                            <li><a href="#" className="hover:text-white transition-colors">Features</a></li>
                            <li><a href="#" className="hover:text-white transition-colors">Pricing</a></li>
                            <li><a href="#" className="hover:text-white transition-colors">How it works</a></li>
                        </ul>
                    </div>

                    {/* Company */}
                    <div>
                        <h4 className={`mb-4 text-[#4fcdfa] ${TYPOGRAPHY.heading}`}>Company</h4>
                        <ul className={`space-y-2 text-sm text-slate-400 ${TYPOGRAPHY.body}`}>
                            <li><a href="#" className="hover:text-white transition-colors">About</a></li>
                            <li><a href="#" className="hover:text-white transition-colors">Blog</a></li>
                            <li><a href="#" className="hover:text-white transition-colors">Careers</a></li>
                        </ul>
                    </div>

                    {/* Legal */}
                    <div>
                        <h4 className={`mb-4 text-[#4fcdfa] ${TYPOGRAPHY.heading}`}>Legal</h4>
                        <ul className={`space-y-2 text-sm text-slate-400 ${TYPOGRAPHY.body}`}>
                            <li><a href="#" className="hover:text-white transition-colors">Privacy</a></li>
                            <li><a href="#" className="hover:text-white transition-colors">Terms</a></li>
                            <li><a href="#" className="hover:text-white transition-colors">Cookies</a></li>
                        </ul>
                    </div>
                </div>

                <div className={`pt-8 border-t border-white/10 flex flex-col md:flex-row justify-between items-center gap-4 text-slate-500 text-sm ${TYPOGRAPHY.body}`}>
                    <p>© {new Date().getFullYear()} Trimy. All rights reserved.</p>
                    <div className="flex gap-4">
                        <a href="#" className="hover:text-white transition-colors">Twitter</a>
                        <a href="#" className="hover:text-white transition-colors">Instagram</a>
                        <a href="#" className="hover:text-white transition-colors">Facebook</a>
                    </div>
                </div>
            </div>
        </footer>
    );
}

// ==================== HOME TAB (Main Content) ====================
function HomeTab() {
    return (
        <>
            <Hero />
            <Features />
            <HowItWorks />
            <Pricing />
        </>
    );
}

// ==================== MAIN LANDING PAGE ====================
export default function LandingPage() {
    const [activeTab, setActiveTab] = useState('home');

    return (
        <div className="min-h-screen">
            <style jsx global>{`
  * {
    font-family: Arial, Helvetica, sans-serif;
  }

  body {
    font-family: Arial, Helvetica, sans-serif;
  }
`}</style>

            
            <Navbar activeTab={activeTab} setActiveTab={setActiveTab} />
            
            {activeTab === 'home' && <HomeTab />}
            {activeTab === 'businesses' && <BusinessesTab />}
            {activeTab === 'contact' && <ContactTab />}
            
            <Footer />
        </div>
    );
}